import React, { useState } from "react"
import axios from "axios"

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000"

function RequestForm() {
    const [formData, setFormData] = useState({
        request_type: "Swap",
        employee_name: "",
        employee_id: "",
        duty_code: "",
        swap_with: "",
        request_dates: [],
        remarks: ""
    })
    const [loading, setLoading] = useState(false)
    const [message, setMessage] = useState({ type: "", text: "" })
    const [selectedDate, setSelectedDate] = useState("")

    const handleChange = (e) => {
        const { name, value } = e.target
        setFormData(prev => ({ ...prev, [name]: value }))
    }

    const handleRequestTypeChange = (type) => {
        setFormData(prev => ({ ...prev, request_type: type, swap_with: type === "Off" ? "" : prev.swap_with }))
    }

    const handleAddDate = () => {
        if (!selectedDate) return
        if (formData.request_dates.includes(selectedDate)) {
            setMessage({ type: "error", text: "Date already added!" })
            return
        }
        setFormData(prev => ({
            ...prev,
            request_dates: [...prev.request_dates, selectedDate]
        }))
        setSelectedDate("")
        setMessage({ type: "", text: "" })
    }

    const handleRemoveDate = (dateToRemove) => {
        setFormData(prev => ({
            ...prev,
            request_dates: prev.request_dates.filter(d => d !== dateToRemove)
        }))
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!formData.employee_name || !formData.employee_id || formData.request_dates.length === 0) {
            setMessage({ type: "error", text: "Please fill in all required fields and select at least one date" })
            return
        }
        if (formData.request_type === "Swap" && !formData.swap_with) {
            setMessage({ type: "error", text: "Please enter who you want to swap with" })
            return
        }
        setLoading(true)
        setMessage({ type: "", text: "" })
        try {
            const payload = {
                ...formData,
                request_dates: formData.request_dates
            }
            const response = await axios.post(`${API_URL}/api/requests`, payload)
            setMessage({ type: "success", text: response.data.message })
            setFormData({ 
                request_type: "Swap", 
                employee_name: "", 
                employee_id: "", 
                duty_code: "", 
                swap_with: "", 
                request_dates: [], 
                remarks: "" 
            })
            setSelectedDate("")
        } catch (error) {
            console.error("Submit error:", error)
            setMessage({ type: "error", text: error.response?.data?.error || "Failed to submit request" })
        } finally { 
            setLoading(false) 
        }
    }

    return (
        <div className="request-form-container">
            <h3>Submit Request</h3>
            
            <div className="request-type-selector">
                <button 
                    type="button" 
                    className={`type-btn swap-btn ${formData.request_type === "Swap" ? "active" : ""}`} 
                    onClick={() => handleRequestTypeChange("Swap")}
                >
                    🔄 Swap
                </button>
                <button 
                    type="button" 
                    className={`type-btn off-btn ${formData.request_type === "Off" ? "active" : ""}`} 
                    onClick={() => handleRequestTypeChange("Off")}
                >
                    📅 Off
                </button>
            </div>
            
            <form onSubmit={handleSubmit} className="request-form">
                <div className="form-group">
                    <label>Employee Name <span className="required">*</span></label>
                    <input 
                        type="text" 
                        name="employee_name" 
                        value={formData.employee_name} 
                        onChange={handleChange} 
                        placeholder="Enter your full name" 
                        required 
                    />
                </div>
                
                <div className="form-group">
                    <label>Employee ID <span className="required">*</span></label>
                    <input 
                        type="text" 
                        name="employee_id" 
                        value={formData.employee_id} 
                        onChange={handleChange} 
                        placeholder="Enter your employee ID" 
                        required 
                    />
                </div>
                
                <div className="form-group">
                    <label>Duty Code</label>
                    <input 
                        type="text" 
                        name="duty_code" 
                        value={formData.duty_code} 
                        onChange={handleChange} 
                        placeholder="e.g., D-1234" 
                    />
                </div>
                
                {formData.request_type === "Swap" && (
                    <div className="form-group">
                        <label>Swap With <span className="required">*</span></label>
                        <input 
                            type="text" 
                            name="swap_with" 
                            value={formData.swap_with} 
                            onChange={handleChange} 
                            placeholder="Enter name of person to swap with" 
                            required 
                        />
                    </div>
                )}
                
                {/* Multiple Date Selection */}
                <div className="form-group">
                    <label>Select Dates <span className="required">*</span></label>
                    <div className="date-picker-wrapper">
                        <input 
                            type="date" 
                            value={selectedDate} 
                            onChange={(e) => setSelectedDate(e.target.value)} 
                            className="date-input"
                        />
                        <button 
                            type="button" 
                            onClick={handleAddDate} 
                            className="add-date-btn"
                            disabled={!selectedDate}
                        >
                            ➕ Add Date
                        </button>
                    </div>
                    
                    {formData.request_dates.length > 0 && (
                        <div className="selected-dates">
                            {formData.request_dates.map((date, index) => (
                                <span key={index} className="date-chip">
                                    {new Date(date).toLocaleDateString("en-US", { 
                                        weekday: "short", 
                                        month: "short", 
                                        day: "numeric", 
                                        year: "numeric" 
                                    })}
                                    <button 
                                        type="button" 
                                        onClick={() => handleRemoveDate(date)} 
                                        className="remove-date-btn"
                                    >
                                        ✕
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}
                </div>
                
                <div className="form-group">
                    <label>Remarks</label>
                    <textarea 
                        name="remarks" 
                        value={formData.remarks} 
                        onChange={handleChange} 
                        placeholder="Any additional remarks..." 
                        rows="3" 
                    />
                </div>
                
                <button type="submit" className="submit-btn" disabled={loading}>
                    {loading ? "Submitting..." : "Submit Request"}
                </button>
                
                {message.text && (
                    <div className={`message ${message.type}`}>
                        {message.text}
                    </div>
                )}
            </form>
        </div>
    )
}

export default RequestForm